import React from 'react';
import { Spinner } from 'flowbite-react';
import { TonePill } from 'src/components/placementDrives/shared';
import { useGetMyTickets } from 'src/hooks/support.hooks';
import { SupportTicketStatus } from 'types';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { HiChatAlt2 } from 'react-icons/hi';

type TabValue = 'unresolved' | 'resolved' | 'all';

const formatTicketDate = (date: string) =>
	new Date(date).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

const labelToSlug = (label: string) => (label || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const MyTicketsSection = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const tabParam = searchParams.get('tab') || 'unresolved';
	const tab: TabValue = tabParam === 'resolved' || tabParam === 'all' ? tabParam : 'unresolved';

	const statusFilter = tab === 'resolved' ? SupportTicketStatus.RESOLVED : tab === 'unresolved' ? SupportTicketStatus.OPEN : '';

	const { data: ticketsData, isLoading } = useGetMyTickets(statusFilter);

	const tickets = ticketsData?.data || [];

	const getStatusColor = (status: SupportTicketStatus | string) => {
		const colors: Record<SupportTicketStatus, string> = {
			[SupportTicketStatus.OPEN]: 'info',
			[SupportTicketStatus.RESOLVED]: 'success',
		};
		return colors[status as SupportTicketStatus] ?? (status === 'in_progress' || status === 'escalated' ? 'info' : status === 'closed' ? 'success' : 'gray');
	};

	const getStatusLabel = (status: string) => (status === 'in_progress' || status === 'escalated' ? 'Open' : status === 'closed' ? 'Resolved' : (status?.charAt(0)?.toUpperCase() ?? '') + (status?.slice(1) ?? ''));

	const setTab = (newTab: TabValue) => {
		const next = new URLSearchParams(searchParams);
		next.set('tickets', 'ticketlisting');
		next.set('tab', newTab);
		navigate(`/support?${next.toString()}`, { replace: true });
	};

	const openTicketInModal = (ticket: any) => {
		const next = new URLSearchParams(searchParams);
		next.set('tickets', 'ticketlisting');
		if (!next.has('tab')) next.set('tab', 'unresolved');
		next.set('step', 'ticketdetails');
		next.set('ticketId', ticket._id);
		const catLabel = typeof ticket.category === 'object' ? (ticket.category?.label || ticket.category?.name) : typeof ticket.category === 'string' ? ticket.category : '';
		const subLabel = typeof ticket.subcategory === 'object' ? (ticket.subcategory?.label || ticket.subcategory?.name) : typeof ticket.subcategory === 'string' ? ticket.subcategory : '';
		if (catLabel) next.set('category', labelToSlug(catLabel));
		if (subLabel) next.set('subcategory', labelToSlug(subLabel));
		navigate(`/support?${next.toString()}`, { replace: true });
	};

	return (
		<div className="dp-rise rounded-[16px] border border-[#E8E8F0] bg-white p-4 shadow-[0_1px_2px_rgba(20,20,60,0.04)] dark:border-gray-700 dark:bg-[#1F2A37] md:p-6">
			<div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
				<h2 className="text-[20px] font-[800] tracking-[-0.02em] text-[#111E2C] dark:text-white">My Support Tickets</h2>
				<div className="flex w-full rounded-full bg-[#ECECF3] p-1 dark:bg-[#111827] md:inline-flex md:w-auto">
					{(['unresolved', 'resolved', 'all'] as const).map(value => (
						<button
							key={value}
							type="button"
							onClick={() => setTab(value)}
							className={`flex-1 rounded-full px-4 py-2 md:py-1.5 text-[13px] outline-none focus:outline-none md:flex-none font-[600] capitalize transition-all duration-200 ${
								tab === value ? 'bg-white text-[#3730A3] shadow-[0_2px_8px_rgba(20,20,60,0.10)] dark:bg-[#18222D] dark:text-[#8F89F5]' : 'text-[#555770] hover:text-[#111E2C] dark:text-[#B0B8C4]'
							}`}
						>
							{value}
						</button>
					))}
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-8">
					<Spinner size="xl" />
				</div>
			) : tickets.length === 0 ? (
				<div className="text-center py-8 text-gray-500 dark:text-gray-400">
					No tickets found. Create a ticket by selecting a question above.
				</div>
			) : (
				<div className="space-y-3">
					{tickets.map((ticket: any) => {
						const categoryLabel = typeof ticket.category === 'object'
							? (ticket.category?.label || ticket.category?.name)
							: typeof ticket.category === 'string' ? ticket.category : '';
						const subcategoryLabel = typeof ticket.subcategory === 'object'
							? (ticket.subcategory?.label || ticket.subcategory?.name)
							: typeof ticket.subcategory === 'string' ? ticket.subcategory : '';
						const categoryDisplay = categoryLabel || subcategoryLabel || 'Support';
						return (
							<button
								key={ticket._id}
								type="button"
								onClick={() => openTicketInModal(ticket)}
								className="dp-lift w-full text-left p-3.5 md:p-4 rounded-[14px] border border-[#E8E8F0] bg-white hover:border-[#DCD9FA] hover:shadow-[0_6px_16px_rgba(79,70,229,0.08)] dark:border-gray-600 dark:bg-[#1F2A37] flex flex-col items-start gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
							>
								<div className="min-w-0 w-full flex-1">
									<p className="font-[700] text-[#111E2C] dark:text-white truncate">{ticket.subject}</p>
									<div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500 dark:text-gray-400">
										<HiChatAlt2 className="w-4 h-4 flex-shrink-0" />
										<span className="truncate">
											{categoryDisplay}
											{categoryDisplay && ' • '}
											{formatTicketDate(ticket.createdAt)}
										</span>
									</div>
								</div>
								<TonePill tone={getStatusColor(ticket.status) === 'success' ? 'good' : getStatusColor(ticket.status) === 'info' ? 'wait' : 'neutral'} className="shrink-0">
									{getStatusLabel(ticket.status)}
								</TonePill>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default MyTicketsSection;


